// This is own implementation of segment classic integration @segment/analytics.js-integration which is not maintained anymore, we needed to create our own version to be able to remove vulnerable dependencies.
// It has limited functionality and only includes features needed for Google Tag Manager integration, but it can be extended in the future if needed. The way how to use it is to import createClassicIntegration function and create integration using it, example of usage can be found in googleTagManagerIntegration.js file.
function noop() {}

function cloneOptions(options) {
    return options ? { ...options } : {};
}

function parseTag(tag) {
    const match = tag.match(/^\s*<(script|img|iframe)\s+([^>]*)>/i);

    if (!match) {
        throw new Error('Unsupported integration tag template.');
    }

    const [, type, attrsSource] = match;
    const attrs = {};
    const attrPattern = /([^\s=]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
    let attrMatch = attrPattern.exec(attrsSource);

    while (attrMatch) {
        attrs[attrMatch[1]] = attrMatch[2] ?? attrMatch[3] ?? '';
        attrMatch = attrPattern.exec(attrsSource);
    }

    return {
        type: type.toLowerCase(),
        attrs
    };
}

function renderAttributes(template, locals) {
    return Object.entries(template.attrs).reduce((attrs, [key, value]) => {
        attrs[key] = value.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, localKey) => locals[localKey]);
        return attrs;
    }, {});
}

function createElement(type, attrs) {
    if (typeof document === 'undefined') {
        return null;
    }

    const element = document.createElement(type);

    Object.entries(attrs).forEach(([key, value]) => {
        if (value == null) {
            return;
        }

        element.setAttribute(key, value);
    });

    return element;
}

function appendElement(element) {
    const parent = document.head || document.body || document.documentElement;

    if (parent) {
        parent.appendChild(element);
    }

    return element;
}

function createClassicIntegration(name) {
    function Integration(options) {
        if (options && typeof options.addIntegration === 'function') {
            return options.addIntegration(Integration);
        }

        this.options = { ...this.defaults, ...cloneOptions(options) };
        this._events = Object.create(null);
        this._queue = [];
        this._ready = false;
        this.ready = this.ready.bind(this);
        this.flush = this.flush.bind(this);
        this.once('ready', this.flush);

        const initialize = this.initialize;
        this.initialize = (...args) => {
            const result = initialize.apply(this, args);
            this.emit('initialize');

            if (this._readyOnInitialize) {
                this.ready();
            }

            return result;
        };
    }

    Integration.prototype.defaults = {};
    Integration.prototype.globals = [];
    Integration.prototype.templates = {};
    Integration.prototype.name = name;
    Integration.prototype.initialize = function () {
        this.ready();
    };
    Integration.prototype.loaded = function () {
        return false;
    };
    Integration.prototype.page = function () {};
    Integration.prototype.track = function () {};
    Integration.prototype.on = function (event, callback) {
        if (!this._events[event]) {
            this._events[event] = [];
        }

        this._events[event].push(callback);
        return this;
    };
    Integration.prototype.once = function (event, callback) {
        const once = (...args) => {
            this.off(event, once);
            callback.apply(this, args);
        };

        return this.on(event, once);
    };
    Integration.prototype.off = function (event, callback) {
        if (!this._events[event]) {
            return this;
        }

        this._events[event] = this._events[event].filter((listener) => listener !== callback);
        return this;
    };
    Integration.prototype.emit = function (event, ...args) {
        const listeners = this._events[event] || [];
        listeners.slice().forEach((listener) => listener.apply(this, args));
        return this;
    };
    Integration.prototype.invoke = function (method, ...args) {
        if (typeof this[method] !== 'function') {
            return undefined;
        }

        if (!this._ready) {
            this.queue(method, args);
            return undefined;
        }

        return this[method](...args);
    };
    Integration.prototype.queue = function (method, args) {
        this._queue.push({ method, args });
    };
    Integration.prototype.flush = function () {
        this._ready = true;

        this._queue.splice(0).forEach((call) => {
            this[call.method](...call.args);
        });
    };
    Integration.prototype.reset = function () {
        if (typeof window === 'undefined') {
            return;
        }

        this.globals.forEach((globalName) => {
            window[globalName] = undefined;
        });
    };
    Integration.prototype.locals = function (locals) {
        const resolvedLocals = locals ? { ...locals } : {};
        const cache = Math.floor(Date.now() / 3600000);

        if (!Object.prototype.hasOwnProperty.call(resolvedLocals, 'cache')) {
            resolvedLocals.cache = cache;
        }

        Object.entries(this.options).forEach(([key, value]) => {
            if (!Object.prototype.hasOwnProperty.call(resolvedLocals, key)) {
                resolvedLocals[key] = value;
            }
        });

        return resolvedLocals;
    };
    Integration.prototype.load = function (name, locals, callback) {
        if (typeof name === 'function') {
            callback = name;
            locals = null;
            name = null;
        } else if (name && typeof name === 'object') {
            callback = locals;
            locals = name;
            name = null;
        } else if (typeof locals === 'function') {
            callback = locals;
            locals = null;
        }

        const templateName = name || 'library';
        const template = this.templates[templateName];

        if (!template) {
            throw new Error(`template "${templateName}" not defined.`);
        }

        const attrs = renderAttributes(template, this.locals(locals));
        const done = callback || noop;
        const element = createElement(template.type, attrs);

        if (!element) {
            done();
            return null;
        }

        if (template.type === 'script') {
            element.addEventListener('load', () => done());
            element.addEventListener('error', () => done(new Error(`Failed to load ${attrs.src || templateName}.`)));
        } else {
            done();
        }

        return appendElement(element);
    };
    Integration.prototype.ready = function () {
        this.emit('ready');
    };

    Integration.option = function (key, value) {
        this.prototype.defaults[key] = value;
        return this;
    };
    Integration.global = function (key) {
        this.prototype.globals.push(key);
        return this;
    };
    Integration.tag = function (tagName, tag) {
        if (tag == null) {
            tag = tagName;
            tagName = 'library';
        }

        this.prototype.templates[tagName] = parseTag(tag);
        return this;
    };
    Integration.assumesPageview = function () {
        this.prototype._assumesPageview = true;
        return this;
    };
    Integration.readyOnInitialize = function () {
        this.prototype._readyOnInitialize = true;
        return this;
    };

    return Integration;
}

module.exports = createClassicIntegration;
